import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import {
  FiArrowRight,
  FiCalendar,
  FiClock,
  FiHeart,
  FiMapPin,
  FiMic,
  FiRefreshCw,
} from "react-icons/fi";
import AnimatedContainer from "../../container/animatedContainer";
import { useGetEventsQuery } from "../../api/event";
import {
  campaign,
  displayTicketType,
  formatCampaignCurrency,
  isCampaignEvent,
  normalizeTicketType,
  sortTickets,
} from "../../config/campaign";

const minimumSupportAmount = 20000;

export function HomePage() {
  const { data, isLoading } = useGetEventsQuery({
    pageSize: 50,
    channel: campaign.channel,
  });
  const currentEvent = data?.list.find((item) =>
    isCampaignEvent(item.title, item.slug),
  );
  const eventLink = currentEvent
    ? `/event/${currentEvent.slug}`
    : `/events?search=${encodeURIComponent(campaign.title)}`;
  const posterImage = currentEvent?.image || campaign.poster;
  const ticketTiers = sortTickets(currentEvent?.pricings ?? []);
  const eventDate = currentEvent ? new Date(currentEvent.date) : null;
  const currency = formatCampaignCurrency(currentEvent?.currency);

  return (
    <AnimatedContainer>
      <div className="min-h-screen bg-[#14261C] text-white">
        <section className="relative min-h-[76svh] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${posterImage}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14261C] via-[#1F3D2C]/88 to-[#E06A1F]/20" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#14261C] to-transparent" />

          <div className="relative mx-auto grid min-h-[76svh] w-full max-w-[1500px] grid-cols-1 items-end gap-10 px-5 pb-12 pt-16 md:px-12 lg:grid-cols-[1fr_380px] lg:px-16">
            <div className="max-w-3xl">
              <img
                src={campaign.logo}
                alt={campaign.fullTitle}
                className="mb-7 w-56 drop-shadow-[0_18px_50px_rgba(224,106,31,0.35)]"
              />
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#F0A93E]">
                Hosted by {campaign.host}
              </p>
              <h1 className="mt-4 max-w-3xl text-4xl font-black uppercase leading-[1.02] text-white sm:text-5xl lg:text-6xl">
                {campaign.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-white/78 sm:text-lg">
                {campaign.host} leads Kigali in a night of praise and worship,
                calling the city to a fresh season of hope.
              </p>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/65">
                Get your sponsorship invitation for the concert. Sponsorship
                invitations are available with limited reserved seats.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={eventLink}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#E06A1F] px-5 py-3 text-xs font-black uppercase tracking-[0.1em] text-white shadow-[0_18px_45px_rgba(224,106,31,0.28)] transition hover:bg-[#F08A3C]"
                >
                  Get invitation
                  <FiArrowRight className="text-lg" />
                </Link>
                <a
                  href="#tickets"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 bg-white/10 px-5 py-3 text-xs font-black uppercase tracking-[0.1em] text-white transition hover:bg-white/15"
                >
                  View prices
                  <FiArrowRight className="text-lg" />
                </a>
              </div>
            </div>

            <div className="hidden overflow-hidden rounded-lg border border-white/15 bg-white/10 p-3 shadow-[0_30px_90px_rgba(0,0,0,0.32)] lg:block">
              <div
                className="aspect-square rounded-md bg-cover bg-center"
                style={{ backgroundImage: `url('${posterImage}')` }}
              />
            </div>
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-4 px-5 py-8 md:px-12 lg:grid-cols-4 lg:px-16">
          <ShowStat
            icon={<FiCalendar />}
            label="Next show"
            value={eventDate ? formatDate(eventDate) : "Coming soon"}
          />
          <ShowStat
            icon={<FiClock />}
            label="Doors Open"
            value={campaign.lineup}
          />
          <ShowStat icon={<FiMic />} label="Host" value={campaign.host} />
          <ShowStat
            icon={<FiMapPin />}
            label="Venue"
            value={currentEvent?.location || campaign.city}
          />
        </section>

        <section
          id="tickets"
          className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-5 px-5 pb-14 md:px-12 lg:grid-cols-[1.05fr_0.95fr] lg:px-16"
        >
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-5 md:p-7">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-[#F0A93E]">
                  Current edition
                </p>
                <h2 className="mt-2 max-w-2xl text-2xl font-black uppercase text-white sm:text-3xl">
                  {currentEvent?.title || campaign.fullTitle}
                </h2>
              </div>
              <Link
                to={eventLink}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-3 text-xs font-black uppercase tracking-[0.1em] text-[#14261C] transition hover:bg-[#FCEEDD]"
              >
                Checkout
                <FiArrowRight />
              </Link>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
              {isLoading ? (
                [1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="h-36 rounded-lg bg-white/10 animate-pulse"
                  />
                ))
              ) : ticketTiers.length === 0 ? (
                <div className="col-span-3 flex min-h-32 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05] text-white/60">
                  Invitations will appear here when the next edition is
                  published.
                </div>
              ) : (
                ticketTiers.map((tier) => (
                  <Link
                    key={tier.id}
                    to={eventLink}
                    className="rounded-lg border border-white/10 bg-white/[0.08] p-5 transition hover:border-[#E06A1F]/70 hover:bg-white/[0.12]"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#F0A93E] text-xl text-[#14261C]">
                      <FiMic />
                    </div>
                    <p className="mt-5 text-xs font-black uppercase tracking-[0.12em] text-white/55">
                      {displayTicketType(tier.type)}
                    </p>
                    <p className="mt-2 text-2xl font-black text-white">
                      {isSupportTier(tier.type)
                        ? `From ${Math.max(
                            tier.price,
                            minimumSupportAmount,
                          ).toLocaleString()}`
                        : tier.price.toLocaleString()}{" "}
                      {currency}
                    </p>
                  </Link>
                ))
              )}
            </div>
          </div>

          <div className="rounded-lg border border-white/10 bg-white text-[#14261C] p-5 md:p-7">
            <div className="flex h-full flex-col justify-between gap-8">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-[#E06A1F]">
                  Show rhythm
                </p>
                <h2 className="mt-2 text-2xl font-black uppercase sm:text-3xl">
                  A night of praise. {campaign.host} leads the way.
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <RhythmItem
                  icon={<FiRefreshCw />}
                  title="A night of praise"
                  body="Isezerano Rishya brings the choir together in worship for one unforgettable evening."
                />
                <RhythmItem
                  icon={<FiMic />}
                  title={`Hosted by ${campaign.host}`}
                  body="The choir leads the congregation in song and worship throughout the night."
                />
                <RhythmItem
                  icon={<FiCalendar />}
                  title="Reserve your seat"
                  body="Choose your place early so the team can plan the worship night with care."
                />
                <RhythmItem
                  icon={<FiHeart />}
                  title="Support the concert"
                  body="Become a sponsor. Sponsorship contribution begins at 20,000 RWF."
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </AnimatedContainer>
  );
}

function ShowStat({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#E06A1F] text-xl text-white">
        {icon}
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
          {label}
        </p>
        <p className="text-base font-black text-white">{value}</p>
      </div>
    </div>
  );
}

function RhythmItem({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex gap-4 rounded-lg border border-[#F0D2AE] bg-[#FBF7EE] p-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#E06A1F] text-xl text-white">
        {icon}
      </div>
      <div>
        <p className="font-black uppercase">{title}</p>
        <p className="mt-1 text-sm leading-6 text-[#14261C]/65">{body}</p>
      </div>
    </div>
  );
}

function isSupportTier(type: string) {
  return normalizeTicketType(type) === "DONATION";
}

function formatDate(date: Date) {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
